import { useEffect, useState } from 'react';
import type { PluginListenerHandle } from '@capacitor/core';
import { PushNotifications } from '@capacitor/push-notifications';
import type { ActionPerformed, PushNotificationSchema } from '@capacitor/push-notifications';
import { useNavigate } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { getNativePlatform, isNativePlatform, waitForNativePlugin } from '@/utils/capacitor';
import { registerFcmToken, resolveNativeFcmToken } from '@/services/pushTokenService';
import { markNotificationRead } from '@/services/notificationService';
import { notificationKeys } from '@/hooks/useNotifications';
import { clientPaths } from '@/utils/clientPaths';

const PUSH_PLUGIN = 'PushNotifications';
const FCM_PLUGIN = 'FCM';
const PUSH_PLATFORMS = new Set(['android', 'ios']);
const ANDROID_PUSH_CHANNEL_ID = 'sporto_default';

async function ensureAndroidDefaultPushChannel(): Promise<void> {
  if (getNativePlatform() !== 'android') {
    return;
  }

  await PushNotifications.createChannel({
    id: ANDROID_PUSH_CHANNEL_ID,
    name: 'Notifikasi Sporto',
    description: 'Notifikasi masuk dari Sporto',
    importance: 5,
    visibility: 1,
    vibration: true,
    lights: true,
  });
}

function canRegisterPush(): boolean {
  return isNativePlatform() && PUSH_PLATFORMS.has(getNativePlatform());
}

function pushData(notification?: PushNotificationSchema): Record<string, string> {
  const data = notification?.data as Record<string, string> | undefined;
  return data ?? {};
}

function resolvePushPath(data: Record<string, string>): string {
  const path = (data.path ?? '').trim();
  if (path.startsWith('/')) {
    return path;
  }
  return clientPaths.notification;
}

export function usePushNotifications(
  userId?: number | null,
  authLoading = false,
  isAuthenticated = false,
  promptAccepted = false
): void {
  const [fcmToken, setFcmToken] = useState<string | null>(null);
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  useEffect(() => {
    if (!canRegisterPush()) {
      return;
    }
    if (authLoading || !isAuthenticated) {
      return;
    }

    let cancelled = false;
    const handles: PluginListenerHandle[] = [];

    const setup = async () => {
      const pushAvailable = await waitForNativePlugin(PUSH_PLUGIN);
      if (cancelled) {
        return;
      }
      if (!pushAvailable) {
        console.error(
          `[Push] Setup failed: plugin is not available on ${getNativePlatform()}. Rebuild after \`npx cap sync\`.`
        );
        return;
      }

      if (getNativePlatform() === 'ios') {
        const fcmAvailable = await waitForNativePlugin(FCM_PLUGIN);
        if (cancelled) {
          return;
        }
        if (!fcmAvailable) {
          console.error(
            '[Push] Setup failed: FCM plugin is not available on iOS. Rebuild after `npx cap sync ios`.'
          );
          return;
        }
      }

      const retainHandle = async (handle: PluginListenerHandle) => {
        if (cancelled) {
          await handle.remove();
          return;
        }
        handles.push(handle);
      };

      try {
        await retainHandle(
          await PushNotifications.addListener('registration', (token) => {
            if (cancelled) return;
            void (async () => {
              try {
                const resolvedToken = await resolveNativeFcmToken(token.value);
                if (cancelled) return;
                setFcmToken(resolvedToken);
              } catch (error) {
                console.error('[Push] Failed to resolve FCM token:', error);
              }
            })();
          })
        );
        await retainHandle(
          await PushNotifications.addListener('registrationError', (error) => {
            console.error('[Push] Registration error:', error);
          })
        );
        await retainHandle(
          await PushNotifications.addListener('pushNotificationReceived', () => {
            void queryClient.invalidateQueries({ queryKey: notificationKeys.unread });
          })
        );
        await retainHandle(
          await PushNotifications.addListener('pushNotificationActionPerformed', (action: ActionPerformed) => {
            const data = pushData(action.notification);
            const notificationId = Number(data.notificationId);
            void (async () => {
              if (Number.isFinite(notificationId) && notificationId > 0) {
                try {
                  await markNotificationRead(notificationId);
                } catch {
                  // tetap buka path meski mark-read gagal
                }
                await queryClient.invalidateQueries({ queryKey: ['notifications'] });
              }
              navigate(resolvePushPath(data));
            })();
          })
        );

        if (cancelled) {
          return;
        }

        try {
          await ensureAndroidDefaultPushChannel();
        } catch (error) {
          console.error('[Push] Failed to create notification channel:', error);
        }

        if (cancelled) {
          return;
        }

        const existing = await PushNotifications.checkPermissions();
        const alreadyGranted = existing.receive === 'granted';
        if (!alreadyGranted && !promptAccepted) {
          return;
        }

        const permission = alreadyGranted
          ? existing
          : await PushNotifications.requestPermissions();
        if (permission.receive !== 'granted') {
          console.warn('[Push] Permission not granted:', permission.receive);
          return;
        }

        await PushNotifications.register();
      } catch (error) {
        console.error('[Push] Setup failed:', error);
      }
    };

    void setup();

    return () => {
      cancelled = true;
      handles.forEach((handle) => {
        void handle.remove();
      });
    };
  }, [authLoading, isAuthenticated, promptAccepted, navigate, queryClient]);

  useEffect(() => {
    if (!canRegisterPush()) {
      return;
    }
    if (authLoading || !isAuthenticated || !fcmToken) {
      return;
    }

    const registerWithBackend = async () => {
      try {
        await registerFcmToken(fcmToken, userId ?? null);
      } catch (error) {
        console.error('[Push] Failed to register FCM token:', error);
      }
    };

    void registerWithBackend();
  }, [fcmToken, userId, authLoading, isAuthenticated]);
}
